export const stripInvalidCharacters = (fileName: string) => {
  // Define the allowed characters: alphanumeric, dashes, and underscores
  const allowedChars = /^[a-zA-Z0-9_-]+$/;

  // Split the filename to separate the extension
  const parts = fileName.split('.');
  const extension = parts.pop(); // Extract the extension
  let name = parts.join('.'); // Rejoin the rest (in case of multiple dots)

  // Replace non-allowed characters in the name part
  name = name
    .split('')
    .filter((char) => allowedChars.test(char))
    .join('');

  // Reconstruct the file name with the sanitized name and original extension
  return `${name}.${extension}`;
};

export const isValidFileName = (fileName: string) => {
  // This regex allows alphanumeric characters, underscores, dashes, spaces, brackets, commas and a single dot for the extension
  const regex = /^[A-Za-z0-9_(){}\[\],-\s]*\.[A-Za-z0-9]+$/;
  return regex.test(fileName);
};

export const replaceExtraDotsWithDash = (fileName: string): string => {
  /**
   * Replace all dots in the "base name" with dashes, keeping only the final dot
   * (the extension separator) intact.
   *
   * Examples:
   * - "a.b.c.pdf" -> "a-b-c.pdf"
   * - "archive.tar.gz" -> "archive-tar.gz"
   * - ".env" -> ".env" (unchanged)
   */
  const lastDotIndex = fileName.lastIndexOf('.');
  if (lastDotIndex <= 0) return fileName;

  const baseName = fileName.substring(0, lastDotIndex);
  const extension = fileName.substring(lastDotIndex + 1);

  const normalizedBaseName = baseName.replaceAll('.', '-');
  return `${normalizedBaseName}.${extension}`;
};

export const generateUniqueFileName = (fileName: string, existingFileNames: string[]): string => {
  // If no conflict, return original name
  if (!existingFileNames.includes(fileName)) {
    return fileName;
  }

  // Extract file name and extension
  const lastDotIndex = fileName.lastIndexOf('.');
  const name = lastDotIndex > 0 ? fileName.substring(0, lastDotIndex) : fileName;
  const extension = lastDotIndex > 0 ? fileName.substring(lastDotIndex) : '';

  // Find the next available number
  let counter = 1;
  let newFileName: string;

  do {
    newFileName = `${name} (${counter})${extension}`;
    counter++;
  } while (existingFileNames.includes(newFileName));

  return newFileName;
};

/**
 * Sanitizes a filename by converting accented characters to their ASCII equivalents
 * and removing/replacing special characters that could cause issues in file systems.
 *
 * Examples:
 * - "José" -> "Jose"
 * - "François" -> "Francois"
 * - "Müller" -> "Muller"
 *
 * @param fileName - The filename to sanitize
 * @returns The sanitized filename with accented characters converted to ASCII
 */
export const sanitizeFileName = (fileName: string): string => {
  if (!fileName) return fileName;

  // Split filename into name and extension
  const lastDotIndex = fileName.lastIndexOf('.');
  const hasExtension = lastDotIndex > 0;
  const baseName = hasExtension ? fileName.substring(0, lastDotIndex) : fileName;
  const extension = hasExtension ? fileName.substring(lastDotIndex) : '';

  // Normalize to NFD (decomposed form) and remove diacritical marks
  // This converts "á" to "a" + combining mark, then we remove the combining marks
  const sanitizedBase = baseName
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '') // Remove combining diacritical marks
    .replace(/[^\w\s-]/g, '') // Remove any remaining special characters except word chars, spaces, and hyphens
    .replace(/\s+/g, '_') // Replace spaces with underscores
    .replace(/_{2,}/g, '_') // Replace multiple underscores with single underscore
    .replace(/^_+|_+$/g, ''); // Remove leading/trailing underscores

  return sanitizedBase ? `${sanitizedBase}${extension}` : `file${extension}`;
};

// Helper function to create a new File object with a different name
export const createRenamedFile = (originalFile: File, newName: string): File => {
  return new File([originalFile], newName, {
    type: originalFile.type,
    lastModified: originalFile.lastModified,
  });
};

export const base64ToBlob = (base64Content: string, contentType: string): Blob => {
  const byteChars = atob(base64Content);
  const bytes = new Uint8Array(byteChars.length);
  for (let i = 0; i < byteChars.length; i++) {
    bytes[i] = byteChars.charCodeAt(i);
  }
  return new Blob([bytes], { type: contentType });
};

/** Hands the browser a blob to save under the given name. */
export const saveBlob = (blob: Blob, fileName: string): void => {
  const url = window.URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = fileName;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  setTimeout(() => window.URL.revokeObjectURL(url), 1000);
};
